// Quantities [MB D8; 03 BR-DM-006]: numeric(14,3) + explicit UoM, held as integer thousandths
// (`milli`) so arithmetic is exact. Fabric canonical unit is the metre; the ONLY yard factor is
// YD→M 0.9144 (legacy used five different constants — never replicate).
import { z } from 'zod';
import { AppError } from '../kernel/errors.ts';
import {
  formatDecimal,
  parseDecimal,
  parseDecimalRounded,
  pow10,
  roundHalfEven,
  toSafeNumber,
} from './decimal.ts';

declare const uomBrand: unique symbol;
/** UoM code (FK to the user-maintained `masters.uom` list, varchar(10)). */
export type Uom = string & { readonly [uomBrand]: true };

const UOM_RE = /^[A-Z][A-Z0-9_]{0,9}$/;

export function isUom(value: unknown): value is Uom {
  return typeof value === 'string' && UOM_RE.test(value);
}

export function toUom(value: string): Uom {
  if (!isUom(value)) throw new RangeError(`invalid uom: ${value}`);
  return value;
}

export const uomSchema = z
  .string()
  .refine(isUom, { message: 'invalid uom' })
  .transform((v) => v as Uom);

/** UoM codes the code itself branches on [assumption: seed codes of `masters.uom`]. */
export const UOM = Object.freeze({
  M: 'M' as Uom,
  CM: 'CM' as Uom,
  YD: 'YD' as Uom,
  EA: 'EA' as Uom,
  KG: 'KG' as Uom,
  G: 'G' as Uom,
});

export const CANONICAL_FABRIC_UOM = UOM.M;
/** `uom.yard_in_m` — read-only constant [02 §11; MB D8]. */
export const YARD_IN_METRES = '0.9144';

export const QTY_SCALE = 3;
/** numeric(14,3): 11 integer digits + 3 decimals. */
export const QTY_MAX_MILLI = 99_999_999_999_999;

export interface Qty {
  readonly milli: number;
  readonly uom: Uom;
}

function checkMilli(milli: number): number {
  if (!Number.isSafeInteger(milli) || Math.abs(milli) > QTY_MAX_MILLI)
    throw new RangeError('quantity outside numeric(14,3)');
  return milli === 0 ? 0 : milli; // normalise -0
}

export function qty(milli: number, uom: Uom): Qty {
  return Object.freeze({ milli: checkMilli(milli), uom });
}

/** "12.5" → 12.500 (strict: more than 3 decimals is refused). */
export function qtyFromDecimal(text: string, uom: Uom): Qty {
  return qty(toSafeNumber(parseDecimal(text, QTY_SCALE)), uom);
}

/** Rounds entered values with more than 3 decimals half-even (e.g. scanner weights). */
export function qtyFromDecimalRounded(text: string, uom: Uom): Qty {
  return qty(toSafeNumber(parseDecimalRounded(text, QTY_SCALE)), uom);
}

/** Machine format for numeric(14,3) columns and exports: "12.500". */
export function qtyToDecimal(q: Qty): string {
  return formatDecimal(BigInt(q.milli), QTY_SCALE);
}

export const qtyDecimalSchema = z.string().regex(/^-?\d{1,11}(\.\d{1,3})?$/);

export const qtySchema = z.object({
  milli: z.number().int().min(-QTY_MAX_MILLI).max(QTY_MAX_MILLI),
  uom: uomSchema,
});

function sameUom(a: Qty, b: Qty): void {
  if (a.uom !== b.uom) {
    throw new AppError('UOM_MISMATCH', {
      messageKey: 'errors.units.uomMismatch',
      params: { left: a.uom, right: b.uom },
    });
  }
}

export function addQty(a: Qty, b: Qty): Qty {
  sameUom(a, b);
  return qty(a.milli + b.milli, a.uom);
}

export function subtractQty(a: Qty, b: Qty): Qty {
  sameUom(a, b);
  return qty(a.milli - b.milli, a.uom);
}

export function negateQty(a: Qty): Qty {
  return qty(-a.milli, a.uom);
}

export function sumQty(items: readonly Qty[], uom: Uom): Qty {
  return items.reduce((acc, q) => addQty(acc, q), qty(0, uom));
}

export function compareQty(a: Qty, b: Qty): -1 | 0 | 1 {
  sameUom(a, b);
  return a.milli < b.milli ? -1 : a.milli > b.milli ? 1 : 0;
}

export function isZeroQty(a: Qty): boolean {
  return a.milli === 0;
}

/** 1 `from` = `factor` `to` (decimal string, exact). Mirrors `masters.uom_conversion`. */
export interface UomConversion {
  readonly from: Uom;
  readonly to: Uom;
  readonly factor: string;
}

/** Built-in exact conversions; everything else comes from `masters.uom_conversion` rows. */
export const BUILT_IN_CONVERSIONS: readonly UomConversion[] = Object.freeze([
  { from: UOM.YD, to: UOM.M, factor: YARD_IN_METRES },
  { from: UOM.CM, to: UOM.M, factor: '0.01' },
  { from: UOM.G, to: UOM.KG, factor: '0.001' },
]);

const FACTOR_SCALE = 12;

/**
 * Refuses any yard conversion whose factor is not exactly 0.9144 (D8/D22). Run on
 * `masters.uom_conversion` writes and migration loads.
 */
export function assertCanonicalYardFactor(conversion: UomConversion): void {
  const ydToM = conversion.from === UOM.YD && conversion.to === UOM.M;
  const mToYd = conversion.from === UOM.M && conversion.to === UOM.YD;
  if (!ydToM && !mToYd) return;
  // M→YD has no finite decimal factor, so it may only be stated as YD→M 0.9144.
  const exact =
    ydToM &&
    parseDecimalRounded(conversion.factor, FACTOR_SCALE) ===
      parseDecimal(YARD_IN_METRES, FACTOR_SCALE);
  if (exact) return;
  throw new AppError('BUSINESS_RULE', {
    messageKey: 'errors.units.yardFactor',
    params: { factor: conversion.factor },
  });
}

function findConversion(
  from: Uom,
  to: Uom,
  table: readonly UomConversion[],
): { num: bigint; den: bigint } | null {
  for (const c of table) {
    const f = parseDecimal(c.factor, FACTOR_SCALE);
    if (f <= 0n) continue;
    if (c.from === from && c.to === to) return { num: f, den: pow10(FACTOR_SCALE) };
    if (c.from === to && c.to === from) return { num: pow10(FACTOR_SCALE), den: f };
  }
  return null;
}

/** Converts exactly, rounding half-even to 3 decimals. YD↔M always uses 0.9144. */
export function convertQty(q: Qty, to: Uom, extra: readonly UomConversion[] = []): Qty {
  if (q.uom === to) return q;
  extra.forEach(assertCanonicalYardFactor);
  const rule = findConversion(q.uom, to, [...BUILT_IN_CONVERSIONS, ...extra]);
  if (!rule) {
    throw new AppError('UOM_MISMATCH', {
      messageKey: 'errors.units.noConversion',
      params: { from: q.uom, to },
    });
  }
  return qty(toSafeNumber(roundHalfEven(BigInt(q.milli) * rule.num, rule.den)), to);
}

/** Ledger form: canonical qty plus what the user entered [03 BR-DM-006]. */
export interface CanonicalQty {
  readonly qty: Qty;
  readonly enteredQty: Qty;
}

export function toCanonical(
  entered: Qty,
  canonical: Uom,
  extra: readonly UomConversion[] = [],
): CanonicalQty {
  return { qty: convertQty(entered, canonical, extra), enteredQty: entered };
}
